// Pure helpers for direct WebSocket draft submission, stored-config
// round-tripping, and preview. Split from connectionDraft.ts to keep each
// editor module below the repository's 800-line cap.
import type { components } from "../../../api/schema";
import { parseHeaders } from "./httpHeaders";

type ConnectionCapability =
  components["schemas"]["ConnectionCapabilityResponse"];
type WebsocketConnectionConfig =
  components["schemas"]["WebsocketConnectionConfig"];

interface WebsocketDraft {
  interfaceKind: string;
  websocketAgentEndSignal: WebsocketConnectionConfig["agent_end_signal"];
  websocketAuthType: components["schemas"]["WebsocketAuthType"];
  websocketCoalesceWindowMs: WebsocketConnectionConfig["coalesce_window_ms"];
  websocketCodec: "" | components["schemas"]["TelephonyCodec"];
  websocketDiscovery: WebsocketConnectionConfig["discovery"];
  websocketEnvelope: "" | components["schemas"]["WebsocketAudioEnvelope"];
  websocketHandshake: WebsocketConnectionConfig["handshake"];
  websocketHeaders: string;
  websocketInbound: WebsocketConnectionConfig["inbound"];
  websocketKeepalivePingMs: WebsocketConnectionConfig["keepalive_ping_ms"];
  websocketOutboundPattern: WebsocketConnectionConfig["outbound_pattern"];
  websocketReconnect: WebsocketConnectionConfig["reconnect"];
  websocketSampleRateHz: string;
  websocketSecretRef: string;
  websocketSubprotocol: WebsocketConnectionConfig["subprotocol"];
  websocketUrl: string;
}

type WebsocketDraftFields = Omit<WebsocketDraft, "interfaceKind"> & {
  interfaceKind: "websocket_chat" | "websocket_voice";
};

type WebsocketConfigResult =
  | { error: string; ok: false }
  | { ok: true; value: WebsocketConnectionConfig };

export function buildWebsocketConfig(
  draft: WebsocketDraft,
  voiceCapability?: ConnectionCapability,
): WebsocketConfigResult {
  const url = draft.websocketUrl.trim();
  if (!url.startsWith("wss://")) {
    return { error: "WebSocket URL must start with wss://.", ok: false };
  }
  const secretRef = draft.websocketSecretRef.trim();
  if (draft.websocketAuthType !== "none" && secretRef.length === 0) {
    return {
      error: "Secret reference is required for bearer and header auth.",
      ok: false,
    };
  }
  const headers = parseHeaders(draft.websocketHeaders);
  if (!headers.ok) {
    return headers;
  }
  const common = {
    auth: {
      secret_ref: draft.websocketAuthType === "none" ? null : secretRef,
      type: draft.websocketAuthType,
    },
    discovery: draft.websocketDiscovery,
    handshake: draft.websocketHandshake,
    headers: headers.value,
    provider: "direct" as const,
    subprotocol: draft.websocketSubprotocol,
    url,
  };

  if (draft.interfaceKind === "websocket_voice") {
    const codec = draft.websocketCodec;
    const envelope = draft.websocketEnvelope;
    const sampleRate = Number(draft.websocketSampleRateHz);
    const codecs = voiceCapability?.codecs ?? [];
    const sampleRatesHz = voiceCapability?.sample_rates_hz ?? [];
    if (
      codec === "" ||
      envelope === "" ||
      !Number.isInteger(sampleRate) ||
      voiceCapability === undefined ||
      !codecs.includes(codec) ||
      !sampleRatesHz.includes(sampleRate)
    ) {
      return {
        error:
          "Audio envelope, codec, and a server-supported sample rate are required.",
        ok: false,
      };
    }
    return {
      ok: true,
      value: {
        ...common,
        agent_end_signal: null,
        audio: { codec, envelope, sample_rate_hz: sampleRate },
        coalesce_window_ms: null,
        inbound: null,
        keepalive_ping_ms: null,
        outbound_pattern: null,
        reconnect: null,
      },
    };
  }

  return {
    ok: true,
    value: {
      ...common,
      agent_end_signal: draft.websocketAgentEndSignal,
      audio: null,
      coalesce_window_ms: draft.websocketCoalesceWindowMs,
      inbound: draft.websocketInbound,
      keepalive_ping_ms: draft.websocketKeepalivePingMs,
      outbound_pattern: draft.websocketOutboundPattern,
      reconnect: draft.websocketReconnect,
    },
  };
}

export function websocketDraftFields(
  config: WebsocketConnectionConfig,
): WebsocketDraftFields {
  return {
    interfaceKind:
      config.audio === null
        ? ("websocket_chat" as const)
        : ("websocket_voice" as const),
    websocketAgentEndSignal: config.agent_end_signal,
    websocketAuthType: config.auth.type,
    websocketCoalesceWindowMs: config.coalesce_window_ms,
    websocketCodec: config.audio?.codec ?? "",
    websocketDiscovery: config.discovery,
    websocketEnvelope: config.audio?.envelope ?? "",
    websocketHandshake: config.handshake,
    websocketHeaders: JSON.stringify(config.headers ?? {}, null, 2),
    websocketInbound: config.inbound,
    websocketKeepalivePingMs: config.keepalive_ping_ms,
    websocketOutboundPattern: config.outbound_pattern,
    websocketReconnect: config.reconnect,
    websocketSampleRateHz: String(config.audio?.sample_rate_hz ?? ""),
    websocketSecretRef: config.auth.secret_ref ?? "",
    websocketSubprotocol: config.subprotocol,
    websocketUrl: config.url,
  };
}

export function websocketConfigPreview(
  draft: WebsocketDraft,
): WebsocketConnectionConfig {
  const headers = parseHeaders(draft.websocketHeaders);
  const common = {
    auth: {
      secret_ref:
        draft.websocketAuthType === "none"
          ? null
          : draft.websocketSecretRef.trim(),
      type: draft.websocketAuthType,
    },
    discovery: draft.websocketDiscovery,
    handshake: draft.websocketHandshake,
    headers: headers.ok ? headers.value : null,
    provider: "direct" as const,
    subprotocol: draft.websocketSubprotocol,
    url: draft.websocketUrl.trim(),
  };
  if (draft.interfaceKind === "websocket_voice") {
    return {
      ...common,
      agent_end_signal: null,
      audio:
        draft.websocketCodec === "" || draft.websocketEnvelope === ""
          ? null
          : {
              codec: draft.websocketCodec,
              envelope: draft.websocketEnvelope,
              sample_rate_hz: Number(draft.websocketSampleRateHz),
            },
      coalesce_window_ms: null,
      inbound: null,
      keepalive_ping_ms: null,
      outbound_pattern: null,
      reconnect: null,
    };
  }
  return {
    ...common,
    agent_end_signal: draft.websocketAgentEndSignal,
    audio: null,
    coalesce_window_ms: draft.websocketCoalesceWindowMs,
    inbound: draft.websocketInbound,
    keepalive_ping_ms: draft.websocketKeepalivePingMs,
    outbound_pattern: draft.websocketOutboundPattern,
    reconnect: draft.websocketReconnect,
  };
}
